/**
 * Organizasyon Yönetimi BÖLÜMLERİ (EP13).
 *
 * Sidebar kaydı (`navigation.ts`) tek bir "Organizasyon Yönetimi" öğesi taşıyor ve kartı
 * *"kanonik sıra korunur"* diyor — oraya ikinci bir üst düzey öğe eklemek kanonik sırayı
 * bozardı. Bu yüzden alt sayfalar kendi kaydında ve `OrganizationLayout` tarafından
 * çiziliyor.
 *
 * Kayıt `navigation.ts` ile AYNI üç kuralı taşıyor: rotası olmayan öğe çizilmez, yetkisi
 * olmayan öğe çizilmez, sıra sabittir. Aynı kuralı iki farklı biçimde yazmak, birinin gün
 * gelip diğerinden ayrışması demekti (kriter 5).
 *
 * ⚠ Görünürlük bir GÜVENLİK SINIRI DEĞİL: gerçek kapı her uçtaki `requirePermission`.
 */

export interface OrganizationSection {
  key: string;
  label: string;
  route: string;
  /** Gerektiren yetki; `null` ise bölüm herkese açık. */
  permission: string | null;
}

/** ⚠ SIRA KANONİKTİR. Yeni bölüm araya değil, kartının söylediği yere girer. */
export const ORGANIZATION_SECTIONS: readonly OrganizationSection[] = [
  {
    key: "profile",
    label: "Profil",
    route: "/app/organizasyon",
    permission: "organization.view",
  },
  {
    key: "members",
    label: "Kullanıcılar",
    route: "/app/organizasyon/kullanicilar",
    permission: "organization.member",
  },
  {
    key: "departments",
    label: "Departmanlar",
    route: "/app/organizasyon/departmanlar",
    permission: "organization.department",
  },
  /**
   * ⚠ Kapı `organization.view`, `organization.role` DEĞİL: kart *"rol/permission
   * GÖRÜNTÜLEME veya yönetme permission'ıyla açılır"* diyor. Yönetme yetkisi istenseydi
   * yalnız görüntüleyebilen kullanıcı sayfaya hiç ulaşamazdı; komutlar sayfa içinde
   * ayrıca kapılı.
   */
  /**
   * ⚠ Kapı `organization.view`: politikayı GÖRME hakkı yönetme hakkından ayrı (CLOS-292).
   * Değiştirme düğmesi sayfa içinde `organization.security` ile kapılı.
   */
  {
    key: "security",
    label: "Güvenlik",
    route: "/app/organizasyon/guvenlik",
    permission: "organization.view",
  },
  /**
   * ⚠ Güvenlikle AYNI kapı (`organization.view`) ve aynı gerekçe (CLOS-1260): hangi
   * uyarının kime gittiği bir sır değil; değiştirmek ise `organization.security` ile
   * sayfa içinde kapılı.
   */
  {
    key: "alerts",
    label: "Uyarı Politikası",
    route: "/app/organizasyon/uyarilar",
    permission: "organization.view",
  },
  {
    key: "roles",
    label: "Roller ve Yetkiler",
    route: "/app/organizasyon/roller",
    permission: "organization.view",
  },
  /**
   * ⚠ KONUM KARARDIR: `Roller ve Yetkiler`in hemen ardında (UI/UX §4, MUST). Gerekçesi
   * kartın kendi cümlesi — *"workflow stage atamaları kullanıcı ve rollerden yararlanır"*.
   * `Katılımcı Rolleri` DIŞ TARAF şablonlarıdır (`contract_role_presets`) ve iç workflow ile
   * kavramsal ilgisi yok; araya girmesi kartın kurduğu komşuluğu bozardı.
   *
   * ⚠ Kapı `workflow.view` (standart sınıf): bir üyenin organizasyonunun hangi süreçten
   * geçtiğini görmesi, rolleri görmesi gibidir. Yönetme ve yayınlama komutları sayfa içinde
   * ve uçlarda `workflow.manage` / `workflow.publish` ile ayrıca kapılı.
   */
  {
    key: "workflows",
    label: "İş Akışları",
    route: "/app/organizasyon/is-akislari",
    permission: "workflow.view",
  },
  /**
   * ⚠ Kapı `contract.share`, `organization.role` DEĞİL: preset listesinin tüketicisi
   * paylaşım/davet ekranlarıdır ve paylaşabilen herkes seçenekleri görebilmeli (uçtaki
   * kapı da bu). Tanımlama/düzenleme komutları sayfa içinde `organization.role` ile
   * ayrıca kapılı.
   */
  {
    key: "participant-roles",
    label: "Katılımcı Rolleri",
    route: "/app/organizasyon/katilimci-rolleri",
    permission: "contract.share",
  },
  /**
   * ⚠ Kapı `organization.audit` — `organization.view` DEĞİL.
   *
   * Diğer bölümlerin çoğu görüntüleme yetkisiyle açılıyor; audit açılmıyor. Kartın kriteri
   * *"ayrı audit görüntüleme permission'ıyla"* diyor ve anahtar `protected` sınıfında:
   * defter bir çalışanın bütün hareketlerini bir başkasına açar. Sekmeyi `organization.view`
   * ile çizseydik, yetkisi olmayan kullanıcıya girip 403 yiyeceği bir kapı göstermiş
   * olurduk — görünürlük ile erişilebilirlik ayrışırdı.
   *
   * ⚠ SIRA SONDA: defter yönetim eylemlerinin değil, onların KAYDININ yeri.
   */
  {
    key: "audit",
    label: "Audit Kayıtları",
    route: "/app/organizasyon/audit",
    permission: "organization.audit",
  },
];

export function visibleOrganizationSections(
  can: (permission: string) => boolean,
  sections: readonly OrganizationSection[] = ORGANIZATION_SECTIONS,
): OrganizationSection[] {
  return sections.filter((s) => s.permission === null || can(s.permission));
}
